// src/components/layout/DashboardLayout.jsx
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useCart } from '@/context/CartContext'
import { useQuery } from '@tanstack/react-query'
import { notificationsApi, messagingApi, loyaltyApi, announcementsApi, adminImpersonationApi } from '@/api/services'
import {
  LayoutDashboard, ShoppingBag, Package, Heart, Bell, Star, Users, Gift,
  ClipboardList, Beaker, MessageSquare, BarChart2, DollarSign, Store,
  FileText, Megaphone, Shield, Scale, Activity, Key, FileCheck,
  TrendingUp, Tag, Truck, LogOut, ChevronRight, Menu, X, CreditCard,
  ShoppingCart, AlertTriangle, BookOpen, UploadCloud, User, MapPin,
  RotateCcw, HelpCircle, FileBarChart, Award, Wand2,
} from 'lucide-react'
import { clsx } from 'clsx'
import AnnouncementBanner from '@/components/common/AnnouncementBanner'
import CurrencySwitcher   from '@/components/common/CurrencySwitcher'

// ── Nav definitions ────────────────────────────────────────────────────────
const buyerNav = [
  { section: 'Shop' },
  { to: '/buyer',          label: 'Dashboard',     icon: LayoutDashboard, end: true },
  { to: '/buyer/orders',   label: 'My Orders',     icon: Package },
  { to: '/buyer/cart',     label: 'Cart',          icon: ShoppingCart, badge: 'cart' },
  { to: '/buyer/wishlist',label: 'Wishlists',     icon: Heart },
  { to: '/buyer/samples',  label: 'Samples',       icon: Beaker },
  { to: '/buyer/rfq',     label: 'RFQ / Enquiries', icon: ClipboardList },
  { section: 'Connect' },
  { to: '/buyer/messages', label: 'Messages',      icon: MessageSquare, badge: 'messages' },
  { to: '/buyer/notifications', label: 'Notifications', icon: Bell, badge: 'notifs' },
  { section: 'Rewards' },
  { to: '/buyer/loyalty',  label: 'My Rewards',    icon: Star, badge: 'loyalty' },
  { to: '/buyer/referrals',label: 'Refer & Earn',  icon: Users },
  { to: '/buyer/gift-cards',label: 'Gift Cards',   icon: Gift },
  { section: 'Account' },
  { to: '/buyer/tier',     label: 'My Tier',       icon: Award },
  { to: '/buyer/swatch-subscription', label: 'Swatch Box', icon: Package },
  { to: '/buyer/design-assistant', label: 'Design Assistant', icon: Wand2 },
  { to: '/buyer/insurance', label: 'Insurance', icon: Shield },
  { section: 'Settings' },
  { to: '/buyer/profile',  label: 'Profile',       icon: User },
  { to: '/buyer/addresses',label: 'Addresses',     icon: MapPin },
]

const supplierNav = [
  { section: 'Catalogue' },
  { to: '/supplier',         label: 'Dashboard',    icon: LayoutDashboard, end: true },
  { to: '/supplier/erp',     label: 'ERP',          icon: Activity },
  { to: '/supplier/products',label: 'My Products',  icon: Package },
  { to: '/supplier/import',  label: 'Bulk Import',  icon: UploadCloud },
  { section: 'Business' },
  { to: '/supplier/orders',  label: 'Orders',       icon: ShoppingBag },
  { to: '/supplier/analytics',label: 'Analytics',  icon: BarChart2 },
  { to: '/supplier/pricing', label: 'Smart Pricing', icon: TrendingUp },
  { to: '/supplier/forecast', label: 'Forecasting', icon: BarChart2 },
  { to: '/supplier/payouts', label: 'Payouts',      icon: DollarSign },
  { section: 'Enquiries' },
  { to: '/supplier/rfq',    label: 'RFQ Requests', icon: ClipboardList },
  { to: '/supplier/samples', label: 'Samples',      icon: Beaker },
  { to: '/supplier/messages',label: 'Messages',     icon: MessageSquare, badge: 'messages' },
  { to: '/supplier/notifications', label: 'Notifications', icon: Bell, badge: 'notifs' },
  { section: 'Store' },
  { to: '/supplier/storefront', label: 'Storefront', icon: Store },
  { to: '/supplier/subscription', label: 'Subscription', icon: CreditCard, badge: 'plan' },
  { section: 'Settings' },
  { to: '/supplier/profile', label: 'Profile',      icon: User },
]

const adminNav = [
  { section: 'Platform' },
  { to: '/admin',          label: 'Dashboard',     icon: LayoutDashboard, end: true },
  { to: '/admin/health',   label: 'Health',        icon: Activity },
  { to: '/admin/analytics',label: 'Analytics',     icon: TrendingUp },
  { section: 'Users' },
  { to: '/admin/users',    label: 'All Users',     icon: Users },
  { section: 'Catalogue' },
  { to: '/admin/products', label: 'Products',      icon: Package },
  { section: 'Commerce' },
  { to: '/admin/orders',   label: 'Orders',        icon: ShoppingBag },
  { to: '/admin/coupons',  label: 'Coupons',       icon: Tag },
  { to: '/admin/payouts',  label: 'Payouts',       icon: DollarSign },
  { to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { section: 'Growth' },
  { to: '/admin/loyalty',  label: 'Loyalty',       icon: Star },
  { to: '/admin/referrals',label: 'Referrals',     icon: Users },
  { to: '/admin/gift-cards',label: 'Gift Cards',   icon: Gift },
  { to: '/admin/abandoned-carts', label: 'Abandoned Carts', icon: ShoppingCart },
  { to: '/admin/promotions',label: 'Promotions',   icon: Megaphone },
  { section: 'Support' },
  { to: '/admin/tickets',  label: 'Tickets',       icon: MessageSquare },
  { to: '/admin/returns',  label: 'Returns',       icon: RotateCcw },
  { to: '/admin/notifications', label: 'Notifications', icon: Bell, badge: 'notifs' },
  { section: 'Trust & Safety' },
  { to: '/admin/fraud',    label: 'Fraud',         icon: AlertTriangle, badge: 'fraud' },
  { to: '/admin/disputes', label: 'Disputes',      icon: Scale, badge: 'disputes' },
  { section: 'Content' },
  { to: '/admin/cms',      label: 'CMS Pages',     icon: FileText },
  { to: '/admin/announcements', label: 'Announcements', icon: Megaphone },
  { to: '/admin/reviews',  label: 'Reviews',       icon: Star },
  { to: '/admin/faqs',     label: 'FAQs',          icon: HelpCircle },
  { section: 'Reports' },
  { to: '/admin/reports/sales', label: 'Sales Report', icon: FileBarChart },
  { to: '/admin/reports/gst',  label: 'GST Filing',   icon: FileText },
  { to: '/admin/supplier-performance', label: 'Supplier Perf.', icon: Award },
  { to: '/admin/compliance', label: 'Compliance',   icon: Shield },
  { section: 'Config' },
  { to: '/admin/audit',    label: 'Audit Trail',   icon: FileCheck },
  { to: '/admin/impersonate', label: 'Impersonation', icon: Key },
  { to: '/admin/tenants',     label: 'Tenants',      icon: Key },
]

const navMap = { buyer: buyerNav, supplier: supplierNav, admin: adminNav }

// ── Main component ─────────────────────────────────────────────────────────
export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth()
  const { count: cartCount } = useCart()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const navigate = useNavigate()

  const { data: notifData } = useQuery({
    queryKey: ['unread-count'],
    queryFn:  () => notificationsApi.unreadCount().then(r => r.data),
    refetchInterval: 30000,
    enabled: !!user,
  })

  const { data: msgData } = useQuery({
    queryKey: ['msg-unread', role],
    queryFn:  () => messagingApi.unreadCount(role).then(r => r.data),
    refetchInterval: 30000,
    enabled: !!user,
  })

  const { data: loyaltyData } = useQuery({
    queryKey: ['loyalty-balance'],
    queryFn:  () => loyaltyApi.balance().then(r => r.data),
    enabled: role === 'buyer',
  })

  const { data: announcements } = useQuery({
    queryKey: ['announcements'],
    queryFn:  () => announcementsApi.active().then(r => r.data.data || []),
    enabled: !!user,
  })

  const { data: impersonation } = useQuery({
    queryKey: ['impersonation-state'],
    queryFn:  () => adminImpersonationApi.list().then(r => r.data),
    enabled: role === 'admin',
  })

  const badges = {
    cart:     cartCount,
    notifs:   notifData?.unread_count,
    messages: msgData?.unread_count,
    loyalty:  loyaltyData?.balance,
    fraud:    null,
    disputes: null,
    plan:     user?.active_plan_name,
  }

  const nav = navMap[role] || []

  return (
    <div className="flex min-h-screen bg-surface">

      {/* ── Impersonation bar ─────────────────────────────────────────── */}
      {impersonation?.active && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-700 to-purple-900 text-white text-xs font-bold py-2 px-6 flex items-center justify-center gap-4">
          <span>👤 Impersonating: <strong>{user?.name}</strong></span>
          <button onClick={() => adminImpersonationApi.stop().then(() => navigate('/admin/impersonation'))}
            className="bg-white/20 border border-white/40 rounded-full px-3 py-1 text-xs">
            Stop
          </button>
        </div>
      )}

      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <aside className={clsx(
        'fixed top-0 left-0 h-full w-60 bg-white border-r border-border z-40 flex flex-col transition-transform duration-300',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      )}>
        {/* Logo */}
        <a href="/" className="flex items-center gap-2.5 px-5 py-5 border-b border-border">
          <img src="/images/logo.png" alt="Fabrinza" className="h-8" onError={e => e.target.style.display='none'} />
          <span className="font-syne font-extrabold text-lg text-gradient">Fabrinza</span>
        </a>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
          {nav.map((item, i) => {
            if (item.section) return (
              <div key={i} className="nav-section">{item.section}</div>
            )
            const Icon = item.icon
            const badge = item.badge ? badges[item.badge] : null
            return (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) => clsx('nav-item', isActive && 'active')}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={16} className="flex-shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                {badge != null && badge !== false && (
                  <span className="ml-auto bg-brand-gradient text-white rounded-full px-2 py-0.5 text-[10px] font-extrabold">
                    {typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : badge}
                  </span>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* User footer */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-brand flex items-center justify-center text-white font-extrabold text-sm flex-shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold truncate">{user?.name}</div>
              <div className="text-xs text-muted capitalize">{user?.role}</div>
            </div>
          </div>
          <button onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold text-muted bg-surface border border-border rounded-xl hover:border-pink hover:text-pink transition-colors">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Sidebar backdrop on mobile */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── Main ──────────────────────────────────────────────────────── */}
      <div className="flex-1 md:ml-60 flex flex-col min-h-screen">

        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white border-b border-border h-14 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setSidebarOpen(v => !v)}>
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <span className="font-syne font-extrabold text-base hidden sm:block text-gradient">
              {role === 'admin' ? 'Admin Console' : role === 'supplier' ? 'Supplier Portal' : 'My Account'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <CurrencySwitcher />
            <NavLink to={`/${role}/notifications`} className="relative text-muted hover:text-pink">
              <Bell size={20} />
              {badges.notifs > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-pink rounded-full text-white text-[9px] font-extrabold flex items-center justify-center">
                  {badges.notifs > 9 ? '9+' : badges.notifs}
                </span>
              )}
            </NavLink>
          </div>
        </header>

        {/* Announcements */}
        {announcements?.length > 0 && <AnnouncementBanner announcements={announcements} />}

        {/* Page content */}
        <main className="flex-1 p-5 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
